import { SAMPLES } from '@/core/samples.ts';
import { DEFAULT_BPM } from '@/core/contants.ts';
import { SampleName, Sequence, SIGNATURE } from '@/types.ts';

interface AudioPlayerParams {
  bpm: number;
  ppq: number;
}

interface AudioPlayerOptions {
  onBarChange: (bar: number | undefined) => void;
  onBeatChange: (beat: number | undefined) => void;
  onLoadingChange: (isLoading: boolean) => void;
}

type PlayOptions = Partial<{
  ms: number,
  gain: number,
  channel: string,
}>;

export class AudioPlayer implements AudioPlayerParams {
  bpm: number;
  ppq: number;
  samples: Record<SampleName, Promise<AudioBuffer> | undefined>;
  channels: Record<string, AudioBufferSourceNode> = {};

  private context: AudioContext = new window.AudioContext();
  private options: Partial<AudioPlayerOptions> = {};
  private startTime: number = 0;
  private firstSampleOffset: number = 0;
  private currentTick: number = -1;
  private currentBeat: number = -1;
  private currentBar: number = -1;
  private tickInterval: number = 0;
  private plannedBars: number = 0;
  private sequence: Sequence = [];
  private signature: SIGNATURE = SIGNATURE['4/4'];
  private signatureDataCache: Record<string, { beats: number; count: number; }> = {};

  constructor(params: AudioPlayerParams, options: Partial<AudioPlayerOptions>) {
    const { bpm, ppq } = params;

    const samples: AudioPlayer['samples'] = Object.entries(SAMPLES)
      .filter(([name]) => name !== 'none')
      .map(([name]) => name)
      .reduce((acc, name) => ({ ...acc, [name]: undefined }), {} as AudioPlayer['samples']);

    this.bpm = bpm;
    this.ppq = ppq;
    this.options = options;
    this.samples = samples;
  }

  public play(
    name: SampleName,
    options: PlayOptions = {},
  ): void {
    if (name === 'none') {
      return;
    }

    const sample = this.samples[name];

    // if sample is not loaded, skip
    if (!sample) {
      return;
    }

    sample
      .then((buffer) => {
        const node = this.context.createBufferSource();
        const { ms, gain, channel } = options;

        node.buffer = buffer;

        const gainNode = this.context.createGain();

        gainNode.gain.value = gain || 1;

        node.connect(gainNode);
        gainNode.connect(this.context.destination);

        if (channel) {
          this.channels[channel]?.stop();
          this.channels[channel] = node;
        }

        node.start(this.msToTime(ms || 0));
      });
  }

  private async loadSamplesAndStart() {
    const sampleNames = [...new Set(this.sequence.flat(2).map((s) => s.sample))]
      .filter((s) => s !== 'none');

    if (this.options.onLoadingChange) {
      this.options.onLoadingChange(true);
    }

    await Promise.all(sampleNames.map((s) => this.preloadSample(s)));

    if (this.options.onLoadingChange) {
      this.options.onLoadingChange(false);
    }

    // figure out if sample offset is needed
    const sampleOffset = this.sequence[0]
      .map((samples) => samples[0])
      .find((sample) => typeof sample.offset !== 'undefined');

    if (sampleOffset && typeof sampleOffset.offset !== 'undefined' && sampleOffset.offset < 0) {
      this.firstSampleOffset = sampleOffset.offset;
    }

    // queue sequence bars
    this.queueBars(0, 3);

    // set interval
    this.tickInterval = window.setInterval(this.tick.bind(this), this.tickMs() / 4);
  }

  public async preloadAndPlay(sample: SampleName, options: PlayOptions = {}) {
    if (sample !== 'none') {
      await this.preloadSample(sample);
      this.play(sample, options);
    }
  }

  public isSampleLoaded(sample: SampleName): Promise<boolean> {
    if (typeof this.samples[sample] === 'undefined') {
      return Promise.resolve(false);
    }

    return Promise.race([
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false))),
      this.samples[sample].then(() => true),
    ]);
  }

  public preloadSample(sample: SampleName): Promise<AudioBuffer> {
    // only preload samples that are not loading
    if (typeof this.samples[sample] === 'undefined') {
      this.samples[sample] = new Promise<AudioBuffer>(async (resolve) => {
        const src = SAMPLES[sample];
        const file = await fetch(src);
        const arrayBuffer = await file.arrayBuffer();

        resolve(await this.context.decodeAudioData(arrayBuffer));
      });
    }

    return this.samples[sample];
  }

  public start(sequence: Sequence, signature: SIGNATURE): void {
    // reset values
    this.context = new window.AudioContext();
    this.startTime = this.context.currentTime;
    this.firstSampleOffset = 0;
    this.currentTick = -1;
    this.currentBeat = -1;
    this.currentBar = -1;

    // set sequence options
    this.sequence = sequence;
    this.signature = signature;

    this.loadSamplesAndStart().then();
  }

  public stop(): void {
    window.clearInterval(this.tickInterval);

    // clear out context
    this.context.close().then(() => this.context = new window.AudioContext());

    // reset note values
    this.options.onBeatChange?.(undefined);
    this.options.onBarChange?.(undefined);
  }

  public setBpm(bpm: number) {
    this.bpm = bpm;
  }

  private tickMs(): number {
    return 60000 / (this.bpm * this.ppq);
  }

  private tick(): void {
    const currentMs = ((this.context.currentTime - this.startTime) * 1000) - (this.firstSampleOffset * -1);
    const tickMs = this.tickMs();

    const tick = Math.floor(currentMs / tickMs);
    const signatureData = this.signateData();

    // update bars and beats
    if (this.currentTick !== tick) {
      this.currentTick = tick;

      const beatTick = Math.floor(tick / (this.ppq / (signatureData.count / 4)));

      if (this.currentBeat !== beatTick) {
        this.currentBeat = beatTick;
        const bpb = signatureData.beats;
        const beat = beatTick % bpb;

        this.options.onBeatChange?.(beat);

        const bar = Math.floor(beatTick / bpb);

        if (bar >= this.plannedBars - 1) {
          this.queueBars(this.plannedBars + 1, this.plannedBars + 2);
        }

        const sequenceBar = bar % this.sequence.length;

        if (this.currentBar !== sequenceBar) {
          this.currentBar = sequenceBar;

          this.options.onBarChange?.(sequenceBar);
        }
      }
    }
  }

  private signateData(): { beats: number; count: number; } {
    if (!this.signatureDataCache[this.signature]) {
      const [beats, count] = this.signature.split('/');

      this.signatureDataCache[this.signature] = {
        beats: parseInt(beats, 10),
        count: parseInt(count, 10),
      };
    }

    return this.signatureDataCache[this.signature];
  }

  private msToTime(ms: number): number {
    return (ms / 1000) + this.startTime + ((this.firstSampleOffset * -1) / 1000);
  }

  private queueBars(from: number, to: number): void {
    if (from === 0) {
      this.startTime = this.context.currentTime;
    }

    this.plannedBars = to;

    const signatureData = this.signateData();
    const beatMs = this.tickMs() * (this.ppq / (signatureData.count / 4));
    const barMs = signatureData.beats * beatMs;

    for (let barIndex = from; barIndex <= to; barIndex++) {
      const sequenceIndex = barIndex % this.sequence.length;
      const sequence = this.sequence[sequenceIndex];

      sequence.forEach((part) => {
        const barStart = barIndex * barMs;

        part.forEach((sample, step) => {
          const stepStart = (barStart + (step * beatMs)) + (sample.offset || 0);

          this.play(sample.sample, { ms: stepStart, gain: sample.gain });
        });
      });
    }
  }
}

export async function createPlayer(options: Partial<AudioPlayerOptions> = {}): Promise<AudioPlayer> {
  const bpm = DEFAULT_BPM;
  const ppq = 4;

  return new AudioPlayer(
    {
      bpm,
      ppq,
    },
    options,
  );
}
